import React, { useState } from "react";
import fadp_cross from "../../../assets/images/fadp-cross.png";
import gdpr_badge from "../../../assets/images/gdpr-badge.png";
import kz_logo from "../../../assets/images/kz_logo.png";
import Button from "../../components/Elements/Button";
import Input from "../../components/Elements/Input";
import { getTranslation } from "../../hooks/utils";
import { csrfToken } from "../../utils/csrfTokenUtils";
import { openPrivacyPage } from "../../utils/privacyPage";
import TermsAndUseContent from "../../utils/TermsAndUseContent";
import DropDown from "../Elements/DropDown";
import TermsAndPolicyModal from "../Modals/TermsAndPolicyModal";
import "./authenticationStyles.css";
import ThanksSection from "./ThanksSection";

const gettanslationStringOnBehalfOfStatus = {
    login: "devise.log_in_header",
    confirmation: "devise.headers.resend_confirmation_instructions",
    unlock: "devise.headers.resend_unlock_instructions",
    forgotPassword: "devise.headers.recover_password",
    signup: "devise.logo_texts.try_kz_free",
};

const loginErrorMessages = [
    "user_exist",
    "invalid_password",
    "not_found_in_database",
];

const formActionURL = {
    login: "/users/login",
    confirmation: "/users/confirmation",
    unlock: "/users/unlock",
    forgotPassword: "/users/password",
    signup: "/signup",
    confirmAccount: "/confirm",
};

// Unlocking itself only happens via the tokenized link Devise emails to the address on file.
const LockedMessage = () => {
    const [state, setState] = useState("idle"); // idle | sending | sent | needEmail | error
    const [sentTo, setSentTo] = useState("");

    const requestUnlockLink = async (e) => {
        e.preventDefault();
        if (state === "sending") return;

        const emailInput = e.currentTarget.closest("form")?.querySelector('input[name="user[email]"]');
        const email = emailInput?.value.trim();
        if (!email) {
            setState("needEmail");
            emailInput?.focus();
            return;
        }

        setState("sending");
        try {
            // Form-encoded because Devise's unlocks controller only responds to HTML.
            const body = new URLSearchParams({ authenticity_token: csrfToken(), "user[email]": email });
            const response = await fetch("/users/unlock", { method: "POST", body });
            // Same confirmation for unknown/unlocked emails so this can't be used to probe accounts.
            if (response.status === 429) {
                setState("error");
                return;
            }
            setSentTo(email);
            setState("sent");
        } catch (err) {
            console.error("Unlock request failed", err);
            setState("error");
        }
    };

    if (state === "sent") {
        return (
            <div>
                If {sentTo} belongs to a locked account, we've emailed it an unlock link.
                Please check your inbox (and spam folder).
            </div>
        );
    }

    // Single wrapper: the parent .alert-devise is d-flex and would split bare text/links into columns.
    return (
        <div>
            {getTranslation("devise.failure.locked1_html")}{" "}
            <a href="#" role="button" onClick={requestUnlockLink}>
                {state === "sending" ? "Sending…" : "Email me an unlock link"}
            </a>{" "}
            to reactivate it.
            {state === "needEmail" && <div>Enter your email above, then click the link again.</div>}
            {state === "error" && <div>Couldn't send the link right now. Please try again in a few minutes.</div>}
        </div>
    );
};

const GetMessage = ({ message }) => {
    let content;
    switch (message) {
        case "send_instructions":
        case "locked":
            content = <LockedMessage />;
            break;
        case "invalid_password":
        case "not_found_in_database":
            content = <>{getTranslation("devise.failure.invalid1")}</>;
            break;
        case "last_attempt":
            content = getTranslation("devise.failure.last_attempt1");
            break;
        default:
            content = "";
    }
    return <>{content}</>;
};

const FadpBadge = ({ onClick }) => (
    <span
        className="d-inline-flex align-items-center justify-content-center fadp-badge"
        onClick={onClick}
        style={{
            cursor: "pointer",
            gap: "6px",
            padding: "3px 8px",
            borderRadius: "4px",
            backgroundColor: "#D0342C",
        }}
    >
        <span
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: "#fff",
            }}
        >
            <img
                src={fadp_cross}
                alt="Swiss cross"
                style={{ width: "15px", height: "15px", borderRadius: "50%", display: "block" }}
            />
        </span>
        <span style={{ color: "#fff", fontWeight: 700, fontSize: "12px", letterSpacing: "0.5px" }}>
            FADP
        </span>
    </span>
);

const Login = ({ props, status }) => {
    const [termsModalStatus, setTermsModalStatus] = useState(false);
    const [signupSuccess, setSignupSuccess] = useState(false);
    const [sessionExpired] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const fromParam = params.get("session_expired") === "true";
        const fromStorage = localStorage.getItem("sessionExpired") === "true";
        const expired = fromParam || fromStorage;
        if (expired) {
            localStorage.removeItem("sessionExpired");
            params.delete("session_expired");
            const newUrl = params.toString()
                ? `${window.location.pathname}?${params.toString()}`
                : window.location.pathname;
            window.history.replaceState({}, "", newUrl);
        }
        return expired;
    });

    // Signup password handling
    const [signupPassword, setSignupPassword] = useState("");
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [emailError, setEmailError] = useState("");

    const { error, messages } = props;
    const passwordRules = {
        minLength: signupPassword.length >= 6,
        uppercase: /[A-Z]/.test(signupPassword),
        lowercase: /[a-z]/.test(signupPassword),
        digit: /\d/.test(signupPassword),
        special: /[^A-Za-z0-9]/.test(signupPassword),
    };

    const allRulesValid = Object.values(passwordRules).every(Boolean);

    const handleSignupSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData(e.target);

        const payload = {
            user: {
                email: formData.get("user[email]"),
                password: formData.get("user[password]"),
                terms_accepted: true,
            },
        };

        try {
            const response = await fetch("/signup", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-CSRF-Token": csrfToken(),
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
                setSignupSuccess(true);
            } else {
                setPasswordErrors(data?.field_errors?.password || []);
                setEmailError(data?.field_errors?.email?.[0] || "");
            }
        } catch (err) {
            console.error("Signup failed", err);
        }
    };

    const getAlertMessage = (type, error) => {
        if (error && error[type]) {
            return <span className="has-error">{error[type].join("")}</span>;
        }
        return "";
    };

    return (
        <div className="login-form-container">
            <form
                acceptCharset="UTF-8"
                className="form-main-auth text-center"
                action={formActionURL[status]}
                method="post"
                onSubmit={status === "signup" ? handleSignupSubmit : undefined}
            >
                {status === "signup" && (
                    <input name="utf8" type="hidden" value="✓" autoComplete="off" />
                )}

                <input type="hidden" value={csrfToken()} name="authenticity_token" />
                {status === "confirmAccount" && (
                    <>
                        <input type="hidden" name="_method" value="patch" />
                        <input type="hidden" name="user[confirmation_token]" value={props?.confirmationToken || ""} />
                    </>
                )}

                <img src={kz_logo} alt="Logo" className="login-form-logo" />

                <div className="login-form-auth-wrapper">
                    {signupSuccess || (messages?.text && messages.text.includes("Thanks")) ? (
                        <ThanksSection />
                    ) : (
                        <>
                            {gettanslationStringOnBehalfOfStatus[status] && (
                                <div
                                    className={`login-form-heading ${status === "signup" ? "signup-header" : ""
                                        }`}
                                >
                                    {getTranslation(gettanslationStringOnBehalfOfStatus[status])}
                                </div>
                            )}

                            {status === "signup" && (
                                <div className="signup-alert">
                                    {`${getTranslation(
                                        "devise.logo_texts.no_card"
                                    )} ${getTranslation("devise.logo_texts.cancel_anytime")}`}
                                </div>
                            )}

                            {sessionExpired && (
                                <div className="session-expired-banner">
                                    <div className="session-expired-text">
                                        <strong>Your session has expired</strong>
                                        Please log in again to continue.
                                    </div>
                                </div>
                            )}

                            {/* Exact match so other Devise notices keep their current (hidden) behavior. */}
                            {props?.messages?.notice === getTranslation("devise.unlocks.unlocked") && (
                                <div className="account-unlocked-banner">
                                    <strong>Your account has been unlocked</strong>
                                    Please log in to continue.
                                </div>
                            )}

                            {props?.messages?.alert && (
                                <div className="d-flex justify-start alert-devise">
                                    <GetMessage message={props.messages.alert} />
                                </div>
                            )}

                            {status === "confirmAccount" && (
                                <div className="login-form-heading">Confirm your account</div>
                            )}

                            {/* Email */}
                            {status !== "confirmAccount" && (
                            <Input
                                type="email"
                                placeholder="Email"
                                name="user[email]"
                                defaultValue={props?.messages?.alert === "locked" ? props?.email || "" : undefined}
                                required
                                errorMessageStatus={error?.email !== undefined}
                            >
                                {getAlertMessage("email", error)}
                                {emailError && (
                                    <span className="has-error">{emailError}</span>
                                )}
                            </Input>
                            )}

                            {status === "confirmAccount" && (
                                <>
                                    <p className="signup-alert">Choose a password to finish setting up your account.</p>
                                    <Input
                                        type="password"
                                        placeholder="Password"
                                        name="user[password]"
                                        required
                                        autoComplete="new-password"
                                        errorMessageStatus={error?.password !== undefined}
                                        onChange={(e) => {
                                            setSignupPassword(e.target.value);
                                            setPasswordTouched(true);
                                        }}
                                    >
                                        {getAlertMessage("password", error)}
                                    </Input>

                                    {passwordTouched && (
                                        <div className="password-rules text-start mb-3">
                                            <p className={passwordRules.minLength ? "valid" : "invalid"}>• Minimum 6 characters</p>
                                            <p className={passwordRules.uppercase ? "valid" : "invalid"}>• At least one uppercase letter</p>
                                            <p className={passwordRules.lowercase ? "valid" : "invalid"}>• At least one lowercase letter</p>
                                            <p className={passwordRules.digit ? "valid" : "invalid"}>• At least one digit</p>
                                            <p className={passwordRules.special ? "valid" : "invalid"}>• At least one special character</p>
                                        </div>
                                    )}

                                    <Input
                                        classList="remember-me-wrapper p-0 text-start mt-3 mb-3"
                                        className="me-1 login-check form-check-input"
                                        type="checkbox"
                                        name="user[terms_accepted]"
                                        id="confirmTermsCheckbox"
                                        value="1"
                                        required
                                    >
                                        <label className="mb-0 small" htmlFor="confirmTermsCheckbox">
                                            I have read and accept the{" "}
                                            <a href="javascript:void(0)" onClick={() => setTermsModalStatus(true)}>
                                                Terms of Service
                                            </a>
                                        </label>
                                    </Input>
                                    {getAlertMessage("terms_accepted", error)}

                                    <Button
                                        classList="login-btn"
                                        type="submit"
                                        text="Confirm Account"
                                        disabled={!allRulesValid}
                                    />

                                    <p className="terms-text large">
                                        Already set a password?{" "}
                                        <a href="/users/login" className="text-decoration-underline">Log In</a>
                                    </p>
                                </>
                            )}

                            {/* LOGIN */}
                            {status === "login" && (
                                <>
                                    <Input
                                        type="password"
                                        placeholder="Password"
                                        name="user[password]"
                                        required
                                    />

                                    <Input
                                        classList="remember-me-wrapper p-0 text-start mt-4"
                                        className="me-1 login-check form-check-input"
                                        type="checkbox"
                                        name="user[remember_me]"
                                        id="rememberMeCheckbox"
                                    >
                                        <span className="d-flex justify-content-between align-items-center w-100">
                                            <label className="mb-0 small" htmlFor="rememberMeCheckbox">Remember Me</label>
                                            <span className="d-flex align-items-center compliance-badges" style={{ gap: "8px" }}>
                                                <img
                                                    src={gdpr_badge}
                                                    alt="GDPR Badge"
                                                    height="25"
                                                    onClick={openPrivacyPage}
                                                    style={{ cursor: "pointer" }}
                                                />
                                                <FadpBadge onClick={openPrivacyPage} />
                                            </span>
                                        </span>
                                    </Input>

                                    <Button
                                        classList="login-btn"
                                        type="submit"
                                        text="Log In"
                                    />

                                    <div className="signup-action-wrapper">
                                        <p>
                                            Don't have an account?{" "}
                                            <a href="/signup" className="text-underline">
                                                Sign Up
                                            </a>
                                        </p>
                                        <p className="text-underline">
                                            <a href="/users/password/new">Forgot password?</a>
                                        </p>
                                    </div>

                                    <div className="terms-wrapper">
                                        <p><a onClick={() => setTermsModalStatus(true)}>
                                            Terms of Service
                                        </a>
                                        </p>
                                        <DropDown
                                            title={<span>options</span>}
                                            classList="d-flex align-self-end"
                                            customClassList="text-dropdown"
                                            optionList={[
                                                {
                                                    url: "/users/confirmation/new",
                                                    title: "Resend Activation Link",
                                                },
                                                { url: "/users/unlock/new", title: "Unlock Account" },
                                                {
                                                    url: "/users/password/new",
                                                    title: "Forgot password?",
                                                },
                                            ]}
                                        />
                                    </div>
                                </>
                            )}

                            {/* CONFIRM / UNLOCK / FORGOT */}
                            {(status === "confirmation" ||
                                status === "unlock" ||
                                status === "forgotPassword") && (
                                    <>
                                        <Button
                                            classList="login-btn"
                                            type="submit"
                                            text={getTranslation(
                                                gettanslationStringOnBehalfOfStatus[status]
                                            )}
                                        />
                                        <div className="d-flex justify-content-between">
                                            <a href="/users/login">Login</a>
                                            <a href="/signup">Sign Up</a>
                                        </div>
                                    </>
                                )}

                            {/* SIGNUP */}
                            {status === "signup" && (
                                <>
                                    <Input
                                        type="password"
                                        placeholder="Password"
                                        name="user[password]"
                                        required
                                        onChange={(e) => {
                                            setSignupPassword(e.target.value);
                                            setPasswordErrors([]);
                                            setPasswordTouched(true);
                                        }}
                                    />

                                    {passwordTouched && (
                                        <div className="password-rules text-start mb-3">
                                            <p className={passwordRules.minLength ? "valid" : "invalid"}>
                                                • Minimum 6 characters
                                            </p>
                                            <p className={passwordRules.uppercase ? "valid" : "invalid"}>
                                                • At least one uppercase letter
                                            </p>
                                            <p className={passwordRules.lowercase ? "valid" : "invalid"}>
                                                • At least one lowercase letter
                                            </p>
                                            <p className={passwordRules.digit ? "valid" : "invalid"}>
                                                • At least one digit
                                            </p>
                                            <p className={passwordRules.special ? "valid" : "invalid"}>
                                                • At least one special character
                                            </p>
                                        </div>
                                    )}

                                    <Input type="hidden" name="user[terms_accepted]" value />

                                    <div className="d-flex justify-content-end align-items-center compliance-badges mb-3" style={{ gap: "8px" }}>
                                        <img
                                            src={gdpr_badge}
                                            alt="GDPR Badge"
                                            height="25"
                                            onClick={openPrivacyPage}
                                            style={{ cursor: "pointer" }}
                                        />
                                        <FadpBadge onClick={openPrivacyPage} />
                                    </div>

                                    <Button
                                        classList="login-btn"
                                        type="submit"
                                        text="Create My Account »"
                                        disabled={!allRulesValid}
                                    />

                                    <p className="terms-text">
                                        By continuing, you agree to the{" "}
                                        <a
                                            href="javascript:void(0)"
                                            onClick={() => setTermsModalStatus(true)}
                                        >
                                            Terms of Service
                                        </a>
                                    </p>

                                    <p className="terms-text large">
                                        Already have an account?{" "}
                                        <a href="/users/login" className="text-decoration-underline">
                                            Log In
                                        </a>
                                    </p>
                                </>
                            )}
                        </>
                    )}
                </div>
            </form>

            <TermsAndPolicyModal
                modalStatus={termsModalStatus}
                modalActiveStatus={setTermsModalStatus}
                title="Terms of Use and Service"
            >
                <TermsAndUseContent />
            </TermsAndPolicyModal>
        </div>
    );
};

export default Login;
